import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import {
  Check,
  ChevronDown,
  CircleDollarSign,
  RotateCcw,
  ShoppingBasket,
  SlidersHorizontal,
  Store,
} from "lucide-react";

import type { Market } from "../../markets/api";
import { useCurrency } from "../../currency/CurrencyContext";
import { DEFAULT_FILTERS } from "../filters";
import type {
  ComparisonFilters,
  HistoryWindow,
  PriceFilterMode,
} from "../types";
import { FilterProfiles } from "./FilterProfiles";

interface FilterPanelProps {
  filters: ComparisonFilters;
  markets: Market[];
  onChange: (filters: ComparisonFilters) => void;
  onRequestAccount: () => void;
}

interface NumericFieldProps {
  label: ReactNode;
  value: number | null;
  suffix?: string;
  min?: number;
  max?: number;
  onChange: (value: number | null) => void;
}

function NumericField({
  label,
  value,
  suffix,
  min,
  max,
  onChange,
}: NumericFieldProps) {
  const { currency, toCurrency, toRubles } = useCurrency();
  const isPrice = suffix === "₽";
  const displayValue =
    isPrice && value !== null ? Number(toCurrency(value).toFixed(2)) : value;
  return (
    <label className="numeric-field">
      <span>{label}</span>
      <span className="numeric-input-wrap">
        <input
          type="number"
          min={min ?? "0"}
          max={max}
          step="any"
          value={displayValue ?? ""}
          placeholder="0"
          onChange={(event) => {
            const rawValue = event.currentTarget.value;
            const nextValue = rawValue === "" ? null : Number(rawValue);
            onChange(
              nextValue === null || !isPrice ? nextValue : toRubles(nextValue),
            );
          }}
        />
        {suffix && <small>{isPrice ? currency.symbol : suffix}</small>}
      </span>
    </label>
  );
}

function FieldHelp({ text }: { text: string }) {
  return (
    <span
      className="metric-help"
      tabIndex={0}
      aria-label="Справка о фильтре"
    >
      ?
      <span className="metric-help-tooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

function Toggle({ label, checked, disabled = false, onChange }: ToggleProps) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <button
        className={`toggle${checked ? " is-checked" : ""}`}
        type="button"
        disabled={disabled}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  );
}

interface MarketSelectProps {
  label: string;
  selected: string[];
  options: Market[];
  onChange: (selected: string[]) => void;
}

function MarketLogo({
  market,
  className,
}: {
  market: Market;
  className: string;
}) {
  if (market.icon_url) {
    return <img className={className} src={market.icon_url} alt="" />;
  }

  return (
    <span className={`${className} is-fallback`} aria-hidden="true">
      {market.name.slice(0, 1)}
    </span>
  );
}

function MarketSelect({
  label,
  selected,
  options,
  onChange,
}: MarketSelectProps) {
  const selectedNames = options
    .filter((market) => selected.includes(market.code))
    .map((market) => market.name);
  const summary =
    selectedNames.length === 1
      ? selectedNames[0]
      : `${label} (${selectedNames.length})`;
  const selectedMarket =
    selected.length === 1
      ? options.find((market) => market.code === selected[0])
      : undefined;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="market-select-trigger" type="button">
          <span className="market-select-summary">
            {selectedMarket && (
              <MarketLogo
                market={selectedMarket}
                className="market-select-trigger-icon"
              />
            )}
            <span>{summary}</span>
          </span>
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="market-select-content"
          align="end"
          sideOffset={6}
        >
          {options.map((market) => {
            const isChecked = selected.includes(market.code);
            return (
              <DropdownMenu.CheckboxItem
                className="market-select-item"
                key={market.code}
                checked={isChecked}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(nextChecked) => {
                  if (nextChecked) {
                    onChange([...selected, market.code]);
                    return;
                  }
                  if (selected.length > 1) {
                    onChange(selected.filter((code) => code !== market.code));
                  }
                }}
              >
                <MarketLogo market={market} className="market-select-icon" />
                <span>{market.name}</span>
                <DropdownMenu.ItemIndicator className="market-select-check">
                  <Check aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.CheckboxItem>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface HistoryWindowSelectProps {
  value: HistoryWindow;
  onChange: (value: HistoryWindow) => void;
}

function HistoryWindowSelect({
  value,
  onChange,
}: HistoryWindowSelectProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="history-window-trigger" type="button">
          <span>{value} дней</span>
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="market-select-content history-window-content"
          align="end"
          sideOffset={6}
        >
          <DropdownMenu.RadioGroup
            value={String(value)}
            onValueChange={(nextValue) =>
              onChange(Number(nextValue) as HistoryWindow)
            }
          >
            {([14, 30, 60] as HistoryWindow[]).map((days) => (
              <DropdownMenu.RadioItem
                className="market-select-item"
                key={days}
                value={String(days)}
              >
                <DropdownMenu.ItemIndicator>
                  <Check aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
                <span>{days} дней</span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const PRICE_FILTER_MODES: Array<{
  value: PriceFilterMode;
  label: string;
}> = [
  { value: "current", label: "Текущая цена" },
  { value: "median", label: "Медианная цена" },
  { value: "average", label: "Средняя цена" },
];

function PriceFilterModeSelect({
  value,
  onChange,
}: {
  value: PriceFilterMode;
  onChange: (value: PriceFilterMode) => void;
}) {
  const selected =
    PRICE_FILTER_MODES.find((option) => option.value === value) ??
    PRICE_FILTER_MODES[0];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="history-window-trigger price-filter-mode-trigger"
          type="button"
        >
          <span>{selected.label}</span>
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="market-select-content history-window-content"
          align="end"
          sideOffset={6}
        >
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(nextValue) =>
              onChange(nextValue as PriceFilterMode)
            }
          >
            {PRICE_FILTER_MODES.map((option) => (
              <DropdownMenu.RadioItem
                className="market-select-item price-filter-mode-item"
                key={option.value}
                value={option.value}
              >
                <span>{option.label}</span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface FilterSectionProps {
  index: string;
  title: string;
  icon: React.ComponentType<{ "aria-hidden"?: "true" }>;
  children: React.ReactNode;
}

function FilterSection({
  index,
  title,
  icon: Icon,
  children,
}: FilterSectionProps) {
  return (
    <section className="filter-section">
      <header>
        <span className="filter-index">{index}</span>
        <Icon aria-hidden="true" />
        <h3>{title}</h3>
      </header>
      <div className="filter-section-body">{children}</div>
    </section>
  );
}

export function FilterPanel({
  filters,
  markets,
  onChange,
  onRequestAccount,
}: FilterPanelProps) {
  const update = <Key extends keyof ComparisonFilters>(
    key: Key,
    value: ComparisonFilters[Key],
  ) => {
    onChange({ ...filters, [key]: value });
  };
  const buyOptions = markets.filter(
    (market) => !market.sell_only && market.code !== "steam",
  );
  const sellOptions = markets;
  const selectPriceFilterMode = (priceFilterMode: PriceFilterMode) => {
    onChange({
      ...filters,
      priceFilterMode,
      minSellPrice: null,
      maxSellPrice: null,
      minMedianPrice: null,
      maxMedianPrice: null,
      minAveragePrice: null,
      maxAveragePrice: null,
    });
  };
  const updateMinDailySales = (value: number | null) => {
    onChange({
      ...filters,
      minDailySales: value,
      minMedianDailySales:
        value !== null &&
        filters.minMedianDailySales !== null &&
        filters.minMedianDailySales < value
          ? value
          : filters.minMedianDailySales,
    });
  };

  return (
    <aside className="filters-panel" aria-label="Параметры поиска">
      <FilterProfiles
        filters={filters}
        onChange={onChange}
        onRequestAccount={onRequestAccount}
      />
      <FilterSection index="01" title="Покупка" icon={ShoppingBasket}>
        <MarketSelect
          label="Маркеты"
          selected={filters.buyMarkets}
          options={buyOptions}
          onChange={(value) => update("buyMarkets", value)}
        />
        <div className="field-grid">
          <NumericField
            label={
              <span className="numeric-field-label">
                Ордеров от
                <FieldHelp text="Количество доступных ордеров на выбранном маркете. «От» оставляет предметы, у которых ордеров не меньше указанного числа — это помогает отсечь сделки с недостаточной глубиной. Поле «Ордеров до» задаёт необязательную верхнюю границу и пригодится для поиска предметов с ограниченным предложением; если она не нужна, оставьте поле пустым." />
              </span>
            }
            value={filters.minBuyOrders}
            suffix="шт."
            onChange={(value) => update("minBuyOrders", value)}
          />
          <NumericField
            label="Ордеров до"
            value={filters.maxBuyOrders}
            suffix="шт."
            onChange={(value) => update("maxBuyOrders", value)}
          />
          <NumericField
            label="Цена от"
            value={filters.minBuyPrice}
            suffix="₽"
            onChange={(value) => update("minBuyPrice", value)}
          />
          <NumericField
            label="Цена до"
            value={filters.maxBuyPrice}
            suffix="₽"
            onChange={(value) => update("maxBuyPrice", value)}
          />
        </div>
      </FilterSection>

      <FilterSection index="02" title="Продажа" icon={Store}>
        <MarketSelect
          label="Маркеты"
          selected={filters.sellMarkets}
          options={sellOptions}
          onChange={(value) => update("sellMarkets", value)}
        />
        <div className="median-filter-heading">
          <span>История продаж · {filters.medianWindowDays} дней</span>
          <HistoryWindowSelect
            value={filters.medianWindowDays}
            onChange={(value) => update("medianWindowDays", value)}
          />
        </div>
        <div className="field-grid active-days-field">
          <NumericField
            label={
              <span className="numeric-field-label">
                Активные дни
                <FieldHelp text="Доля дней с хотя бы одной продажей за выбранный период истории. Например, 70% за 30 дней означает продажи минимум в 21 день." />
              </span>
            }
            value={filters.minActiveDaysPct}
            suffix="%"
            max={100}
            onChange={(value) => update("minActiveDaysPct", value)}
          />
          <NumericField
            label={
              <span className="numeric-field-label">
                Продаж в сутки
                <FieldHelp text="Минимальное среднее число продаж в день за выбранные 14, 30 или 60 дней. В расчёт входят и дни без продаж. Например, значение 10 за 14 дней оставит предметы, у которых за этот период в среднем продано не меньше 10 штук в день." />
              </span>
            }
            value={filters.minDailySales}
            suffix="шт."
            onChange={updateMinDailySales}
          />
        </div>
        <div className="field-grid">
          <NumericField
            label={
              <span className="numeric-field-label">
                Ордеров от
                <FieldHelp text="Количество доступных ордеров на выбранном маркете. «От» оставляет предметы, у которых ордеров не меньше указанного числа — это помогает отсечь сделки с недостаточной глубиной. Поле «Ордеров до» задаёт необязательную верхнюю границу и пригодится для поиска предметов с ограниченным предложением; если она не нужна, оставьте поле пустым." />
              </span>
            }
            value={filters.minSellOrders}
            suffix="шт."
            onChange={(value) => update("minSellOrders", value)}
          />
          <NumericField
            label="Ордеров до"
            value={filters.maxSellOrders}
            suffix="шт."
            onChange={(value) => update("maxSellOrders", value)}
          />
        </div>
        <div className="price-filter-group">
          <div className="price-filter-heading">
            <PriceFilterModeSelect
              value={filters.priceFilterMode}
              onChange={selectPriceFilterMode}
            />
          </div>

          <AnimatePresence initial={false} mode="wait">
            {filters.priceFilterMode === "current" && (
              <motion.div
                animate={{ opacity: 1, height: "auto" }}
                className="price-filter-mode-content"
                exit={{
                  opacity: 0,
                  height: 0,
                  transition: { duration: 0.01 },
                }}
                initial={{ opacity: 0, height: 0 }}
                key="current"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <div className="field-grid">
                  <NumericField
                    label="Текущая цена от"
                    value={filters.minSellPrice}
                    suffix="₽"
                    onChange={(value) => update("minSellPrice", value)}
                  />
                  <NumericField
                    label="Текущая цена до"
                    value={filters.maxSellPrice}
                    suffix="₽"
                    onChange={(value) => update("maxSellPrice", value)}
                  />
                </div>
              </motion.div>
            )}

            {filters.priceFilterMode === "median" && (
              <motion.div
                animate={{ opacity: 1, height: "auto" }}
                className="price-filter-mode-content"
                exit={{
                  opacity: 0,
                  height: 0,
                  transition: { duration: 0.01 },
                }}
                initial={{ opacity: 0, height: 0 }}
                key="median"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <div className="median-filter-group">
                  <div className="median-filter-heading">
                    <span>Медианная цена · {filters.medianWindowDays} дней</span>
                  </div>
                  <Toggle
                    label="Вес продаж"
                    checked={filters.medianWeighted}
                    onChange={(value) => update("medianWeighted", value)}
                  />
                  <div className="field-grid">
                    <NumericField
                      label="Медиана от"
                      value={filters.minMedianPrice}
                      suffix="₽"
                      onChange={(value) => update("minMedianPrice", value)}
                    />
                    <NumericField
                      label="Медиана до"
                      value={filters.maxMedianPrice}
                      suffix="₽"
                      onChange={(value) => update("maxMedianPrice", value)}
                    />
                  </div>
                  <NumericField
                    label={
                      <span className="numeric-field-label">
                        Медиана продаж
                        <FieldHelp text="Типичное количество продаж за день: медиана всех дневных продаж за выбранный период, включая дни без продаж. В отличие от среднего она не даёт одному всплеску продаж сильно завысить результат. Значение не может быть меньше общего фильтра «Продаж в сутки»." />
                      </span>
                    }
                    value={filters.minMedianDailySales}
                    min={filters.minDailySales ?? 0}
                    suffix="шт."
                    onChange={(value) =>
                      update(
                        "minMedianDailySales",
                        value === null
                          ? null
                          : Math.max(value, filters.minDailySales ?? 0),
                      )
                    }
                  />
                </div>
              </motion.div>
            )}

            {filters.priceFilterMode === "average" && (
              <motion.div
                animate={{ opacity: 1, height: "auto" }}
                className="price-filter-mode-content"
                exit={{
                  opacity: 0,
                  height: 0,
                  transition: { duration: 0.01 },
                }}
                initial={{ opacity: 0, height: 0 }}
                key="average"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <div className="median-filter-group">
                  <div className="median-filter-heading">
                    <span>Период истории · {filters.medianWindowDays} дней</span>
                  </div>
                  <div className="field-grid">
                    <NumericField
                      label="Средняя цена от"
                      value={filters.minAveragePrice}
                      suffix="₽"
                      onChange={(value) => update("minAveragePrice", value)}
                    />
                    <NumericField
                      label="Средняя цена до"
                      value={filters.maxAveragePrice}
                      suffix="₽"
                      onChange={(value) => update("maxAveragePrice", value)}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </FilterSection>

      <FilterSection index="03" title="Прибыль" icon={CircleDollarSign}>
        <div className="field-grid">
          <NumericField
            label={
              <span className="numeric-field-label">
                ROI от
                <FieldHelp text="ROI — чистая доходность сделки относительно цены покупки. Формула: (цена продажи после комиссии − цена покупки) ÷ цена покупки × 100%. «ROI от» задаёт минимальную доходность, а «ROI до» — необязательную верхнюю границу." />
              </span>
            }
            value={filters.minProfitPct}
            suffix="%"
            onChange={(value) => update("minProfitPct", value)}
          />
          <NumericField
            label="ROI до"
            value={filters.maxProfitPct}
            suffix="%"
            onChange={(value) => update("maxProfitPct", value)}
          />
          <NumericField
            label={
              <span className="numeric-field-label">
                Выход до
                <FieldHelp text="Максимальное ожидаемое время продажи безопасного объёма. Расчёт использует историю продаж и консервативно предполагает, что ваши предметы займут не более 10% дневного объёма рынка. Например, значение 3 оставит сделки с ожидаемым выходом не дольше трёх дней. Сделки без достаточной истории не пройдут этот фильтр." />
              </span>
            }
            value={filters.maxExpectedSaleDays}
            suffix="дн."
            onChange={(value) => update("maxExpectedSaleDays", value)}
          />
          <NumericField
            label={
              <span className="numeric-field-label">
                Профит объёма
                <FieldHelp text="Минимальная чистая прибыль со всего безопасного объёма закупки. Она равна прибыли с одной штуки, умноженной на безопасное количество. Это количество ограничивается доступными ордерами и объёмом, который статистически можно продать без сильного влияния на рынок." />
              </span>
            }
            value={filters.minSafeVolumeProfit}
            suffix="₽"
            onChange={(value) => update("minSafeVolumeProfit", value)}
          />
        </div>
      </FilterSection>

      <FilterSection index="04" title="Выдача" icon={SlidersHorizontal}>
        <Toggle
          label="Убрать отрицательные сделки"
          checked={filters.hideNegativeDeals}
          onChange={(value) => update("hideNegativeDeals", value)}
        />
        <Toggle
          label="Без стикеров"
          checked={filters.hideStickers}
          onChange={(value) => update("hideStickers", value)}
        />
      </FilterSection>

      <button
        className="reset-filters"
        type="button"
        onClick={() => onChange(DEFAULT_FILTERS)}
      >
        <RotateCcw aria-hidden="true" />
        Сбросить фильтры
      </button>
    </aside>
  );
}
